import React, { useEffect, useRef, useState } from 'react'
import { useTrans, useUtente } from 'hooks'
import { PaginaType } from 'utils/types'
import { scrollTo } from 'utils/safe'
import { LandingProdottoButton, WContainer, MetaSeo } from '@components/atoms'
import {
  LandingProdottoHead,
  LandingProdottoInfo,
  LandingProdottoIngredienti,
  LandingProdottoProdotti,
  Page,
} from 'components/organisms'
import styles from './LandingProdottoTemplate.module.sass'
import { useSelector } from 'react-redux'
import CategorieProdottoCorrelate from '@components/organisms/CategorieProdottoCorrelate/CategorieProdottoCorrelate'
import useGTM from '../../../hooks/useGTM'

interface Props {
  pagina: PaginaType
  categoria: any
  variant?: '' | 'latti' | 'bio'
}

const LandingProdottoTemplate = (props: Props) => {
  const { pagina, categoria, variant = '' } = props

  const t = useTrans()

  const { menu } = useSelector((state) => state.menu)
  const landingMenu = menu?.find((item) => item.key === 'landing-prodotti')
  const categoriaMenu = landingMenu?.children?.find((item) => item.key === categoria.slug)
  const items = categoriaMenu?.children?.concat({ pk: 3, style: 'prodotti' }) || [
    { pk: 3, style: 'prodotti' },
  ]
  const headRef = useRef<HTMLDivElement>()
  const sectionRefs = useRef([])
  const [fixedLinksVisible, setFixedLinksVisible] = useState(false)

  const { trackingGAevent } = useGTM()
  const { utente } = useUtente()
  const toolbarPresente = utente && (utente.isStaff || utente.isImpersonate)

  const scrollToRef = (ref) => {
    if (ref) scrollTo(ref, toolbarPresente, 10, window.innerWidth < 992, window.scrollY)
  }

  const sectionLinksBlock = (variant: '' | 'white' = '') => (
    <>
      {items.length > 0 &&
        items.map((x, i) => (
          <div className={styles.buttonWrap}>
            <LandingProdottoButton
              key={`landing-prodotto-button-${x.pk}`}
              label={x.style === 'info' ? t("Perchè sceglierli") : x.style === 'ingredienti' ? t("Come li facciamo") : t(x.style)}
              variant={variant}
              onClick={() => {
                trackingGAevent('16', pagina.titolo, 'head', t(x.style))
                scrollToRef(sectionRefs?.current?.[i])
              }}
            />
          </div>
        ))}
    </>
  )

  const handleScroll = () => {
    setFixedLinksVisible(
      window.scrollY > (headRef && headRef.current ? headRef.current?.clientHeight || 0 : 0)
    )
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <Page pagina={pagina} item={categoria}>
      <style jsx>{`
        @font-face {
          font-family: 'SmartKid';
          src: url('/fonts/SmartKid-Bold.eot');
          src: url('/fonts/SmartKid-Bold.eot?#iefix') format('embedded-opentype'),
            url('/fonts/SmartKid-Bold.svg#Smart_Kid') format('svg'),
            url('/fonts/SmartKid-Bold.ttf') format('truetype'),
            url('/fonts/SmartKid-Bold.woff') format('woff'),
            url('/fonts/SmartKid-Bold.woff2') format('woff2');
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
      `}</style>
      {pagina?.seo && <MetaSeo seo={pagina.seo} />}
      <div
        className={`${styles.landingProdottoTemplate} ${
          variant ? styles[`landingProdottoTemplate--${variant}`] : ''
        }`}
      >
        <div
          className={`${styles.fixedSectionLinks} ${
            fixedLinksVisible ? styles['fixedSectionLinks--visible'] : ''
          }`}
        >
          {sectionLinksBlock()}
        </div>
        <div ref={headRef}>
          <LandingProdottoHead
            breadcrumbs={pagina.breadcrumbs}
            title={pagina.titolo}
            description={pagina.descrizione}
            variant={variant}
            image={pagina.mainImage}
            imageMobile={pagina.previewImage}
          >
            <div className={styles.topSectionLinks}>{sectionLinksBlock('white')}</div>
          </LandingProdottoHead>
        </div>
        {items.map((item) => (
          <div key={item.pk} ref={(element) => sectionRefs.current.push(element)}>
            {item.style === 'info' && <LandingProdottoInfo item={item} variant={variant} />}
            {item.style === 'ingredienti' && (
              <LandingProdottoIngredienti categoria={categoria} item={item} variant={variant} paginaTitolo={pagina.titolo} />
            )}
            {item.style === 'prodotti' && <LandingProdottoProdotti categoria={categoria} paginaTitolo={pagina.titolo} />}
          </div>
        ))}
        <div className={styles.categories}>
          <WContainer>
            <CategorieProdottoCorrelate
              item={{
                name: t('Scopri le altre linee di prodotto'),
                children: categoria.siblings.map((c) => ({
                  pk: c.pk,
                  name: c.titolo,
                  image: c.previewImage,
                  url: c.url,
                })),
              }}
              paginaTitolo={pagina.titolo}
            />
          </WContainer>
        </div>
      </div>
    </Page>
  )
}

export default LandingProdottoTemplate
